import React, { useEffect, useState } from 'react';
import { Check, X, AlertTriangle, Info, X as CloseIcon } from 'lucide-react';
import { Notification } from '@nusrif/types';

interface NotificationToastProps {
  notification: Notification;
  onClose: () => void;
  index: number;
}

const getNotificationStyles = (type: Notification['type']) => {
  switch (type) {
    case 'success':
      return {
        bgColor: 'bg-success',
        icon: Check,
        iconColor: 'text-white',
      };
    case 'error':
      return {
        bgColor: 'bg-red-500',
        icon: X,
        iconColor: 'text-white',
      };
    case 'warning':
      return {
        bgColor: 'bg-yellow-500',
        icon: AlertTriangle,
        iconColor: 'text-white',
      };
    case 'info':
      return {
        bgColor: 'bg-blue-500',
        icon: Info,
        iconColor: 'text-white',
      };
    default:
      return {
        bgColor: 'bg-gray-500',
        icon: Info,
        iconColor: 'text-white',
      };
  }
};

export const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  index,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const styles = getNotificationStyles(notification.type);
  const IconComponent = styles.icon;

  useEffect(() => {
    // Trigger entrance animation
    setShouldRender(true);
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      onClose();
    }, 300);
  };

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed top-20 left-5 right-5 z-[60] transition-all duration-300 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
      style={{ top: `${5.5 + index * 5}rem` }}
    >
      <div className={`${styles.bgColor} text-white p-4 rounded-lg shadow-lg flex items-center gap-3`}>
        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
          <IconComponent className={`w-4 h-4 ${styles.iconColor}`} />
        </div>
        <div className="flex-1">
          <p className="font-medium">{notification.title}</p>
          {notification.message && (
            <p className="text-sm opacity-90">{notification.message}</p>
          )}
        </div>
        {notification.action && (
          <button
            onClick={notification.action.onClick}
            className="text-sm font-medium bg-white/20 hover:bg-white/30 px-3 py-1 rounded transition-colors"
          >
            {notification.action.label}
          </button>
        )}
        <button
          onClick={handleClose}
          className="w-6 h-6 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
        >
          <CloseIcon className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}; 